import { useState, useEffect } from 'react';
import COUNTRIES from '@/data/countries.json';

const SESSION_KEY = 'geoInferCountry';

// UN-5 slug → display name, keep in sync with frontend/src/data/regions.json
const REGION_NAMES = {
  oceania: 'Oceania',
  asia: 'Asia',
  americas: 'Americas',
  europe: 'Europe',
  africa: 'Africa',
};

function getCountryName(iso2) {
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    return dn.of(iso2) || iso2;
  } catch {
    return iso2;
  }
}

// Derive a UN-5 region from the browser's IANA timezone. Works even when the
// backend can't resolve country (visitor's IP hidden by VPN returning XX, or
// CF header stripped by a corporate proxy). Runs fully client-side so there's
// no second backend hop.
//
// Intl.DateTimeFormat().resolvedOptions().timeZone returns strings like
// "Australia/Sydney", "America/New_York", "Europe/London" — the first segment
// is the IANA tz "continent" which maps cleanly onto UN-5 for ~99% of zones.
function inferRegionFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const continent = tz.split('/')[0].toLowerCase();
    const map = {
      australia: 'oceania',  // Australia/Sydney, Australia/Melbourne, …
      pacific:   'oceania',  // Pacific/Auckland, Pacific/Fiji, …
      america:   'americas', // America/New_York, America/Sao_Paulo, …
      europe:    'europe',   // Europe/London, Europe/Berlin, …
      africa:    'africa',   // Africa/Cairo, …
      asia:      'asia',     // Asia/Tokyo, …
      // atlantic/indian/antarctica are ambiguous or empty — leave as null
    };
    return map[continent] || null;
  } catch {
    return null;
  }
}

/**
 * Geolocates the visitor via backend IP lookup, with a timezone-based
 * region fallback.
 *
 * Returns { iso2, name, region, mapPath, ready }
 *
 * iso2     — lowercase ISO2 (e.g. "au") when inference succeeded and the
 *            country is in countries.json, otherwise **null**. There is no
 *            AU fallback — callers must treat null as "unknown".
 * name     — country name ("Australia" / "United States" / etc.) when
 *            iso2 is set, else region name ("Oceania" / "Europe" / etc.)
 *            when a region was inferred from timezone, else null.
 * region   — UN-5 slug ("oceania" / "asia" / "americas" / "europe" /
 *            "africa") when country fails but timezone succeeds. Always
 *            null if iso2 is set (redundant then).
 * mapPath  — priority: "/map/<iso2>" > "/map/region/<slug>" > "/directory"
 * ready    — false while the backend fetch is in-flight, true once resolved.
 */
const useInferredCountry = () => {
  const [iso2, setIso2] = useState(null);
  const [name, setName] = useState(null);
  const [region, setRegion] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Timezone-based region is the immediate fallback — computed synchronously
    // so it's available even before the backend hop completes. Overwritten
    // later if country inference succeeds.
    const tzRegion = inferRegionFromTimezone();
    if (tzRegion) setRegion(tzRegion);

    // Session cache: only persist successful country inferences. We used to
    // cache a hardcoded {iso2: 'au'} on failure, which poisoned the
    // subsequent UX for every VPN'd / anonymised visitor.
    try {
      const cached = sessionStorage.getItem(SESSION_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.iso2 && COUNTRIES[parsed.iso2.toUpperCase()]) {
          setIso2(parsed.iso2);
          setName(parsed.name);
          setReady(true);
          return;
        }
      }
    } catch {
      // ignore parse errors
    }

    fetch('/api/geo/infer')
      .then(res => res.json())
      .then(data => {
        const code = data?.country_code;
        if (code && COUNTRIES[code.toUpperCase()]) {
          const lower = code.toLowerCase();
          const displayName = getCountryName(code.toUpperCase());
          setIso2(lower);
          setName(displayName);
          try {
            sessionStorage.setItem(SESSION_KEY, JSON.stringify({ iso2: lower, name: displayName }));
          } catch {
            // ignore quota errors
          }
        }
        // Unknown country → stick with the timezone region set at mount. Don't
        // cache fallbacks — re-infer on each session in case the backend
        // recovers or the visitor's routing changes.
      })
      .catch(() => {
        // silent — leave null so callers fall through to region/directory
      })
      .finally(() => {
        setReady(true);
      });
  }, []);

  // Country wins when set; timezone region is the fallback name + link.
  const effectiveName = iso2 ? name : (region ? REGION_NAMES[region] : null);
  const mapPath = iso2
    ? `/map/${iso2}`
    : (region ? `/map/region/${region}` : '/directory');

  return {
    iso2,
    name: effectiveName,
    region: iso2 ? null : region,
    mapPath,
    ready,
  };
};

export default useInferredCountry;
